import { getFormValues } from "redux-form";
import { all, put, takeEvery, call, select, spawn } from "redux-saga/effects";
import config from "@config";
import actionCreators from "@modules/service/actionCreator";
import {
  UpdateStaffGroup,
  ReassignStaffGroup,
  EnableStaffGroup,
  AddStaffGroup,
  EditStaffGroup,
  GetStaffGroups,
  GetTotalStaffGroups,
  GetStaffGroupsSchema,
  SaveStaffGroupConfiguration,
  GetStaffGroupsConfiguration,
  GetDataSecuritySettings,
  UpdateDataSecuritySettings,
  GetEmployeeSecuritySchema,
  SetStaffGroupsSort,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import http from "@http";
import * as serviceSelectors from "@modules/service/selectors";
import { parseSchema } from "@utils/schema";

import serviceSettingsSaga from "./service-settings";
import shiftPatternsSaga from "./shift-patterns";
import nurseDeclinedReasonSagas from "./nurse-declined-reasons";
import eapSignpostingSaga from "./eap-signposting";
import absenceReasonsSaga from "./absence-reasons";
import specialContactsSaga from "./special-contacts";
import specialContactsTypesSaga from "./special-contacts-types";
import userPermissionsSaga from "./user-permissions";
import reportingParametersSaga from "./reporting-parameters";
import employeeIdentifySaga from "./employee-identity";
import standardQuestionsSaga from "./standard-questions";
import dynamicQuestionsSaga from "./dynamic-questions";
import managerSmsSettingsSaga from "./manager-sms-settings";
import managerSmsNotificationsSaga from "./manager-sms-notifications";
import managerPagerNotificationsSaga from "./manager-pager-notifications";
import emailNotificationsSaga from "./email-notifications";
import employeeSmsSettingsSaga from "./employee-sms-settings";
import employeeSmsNotificationsSaga from "./employee-sms-notifications";
import emailControlsSaga from "./email-controls";
import systemAccessSaga from "./system-access";
import defaultStandardScriptsSaga from "./default-standard-scripts";
import departmentSpecificScriptsSaga from "./department-specific-scripts";
import alertsSaga from "./alerts";
import alertAttachmentsSaga from "./alert-attachments";
import approvableChangeSaga from "./approvable-change";
import referralsSaga from "./referrals";
import rtwSaga from "./rtw";
import rtwAttachmentsSaga from "./rtw-attachments";
import absenceMonitoringSaga from "./absence-monitoring";
import reasonSpecificScriptsSaga from "./reason-specific-scripts";
import userAccountsSaga from "./user-accounts";

export function* onGetEmployeeSecuritySchema() {
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/schema-registry/DataSecuritySettingsDTO`,
      {
        headers: {
          Accept: "application/json+schema",
        },
      },
    );
    yield put(actionCreators.getEmployeeSecuritySchemaSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getEmployeeSecuritySchemaError(e.response));
  }
}

export function* onGetDataSettings({ payload }) {
  const clientId = payload.clientId;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v2/service-admin/client/${clientId}/service-config/absence-management/data-security-settings`,
    );
    yield put(actionCreators.getDataSecuritySettingsSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getDataSecuritySettingsError(e.response));
  }
}

export function* onUpdateDataSettings({ payload }) {
  try {
    const clientId = payload.clientId;
    delete payload.clientId;

    const response = yield call(
      http.put,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/absence-management/data-security-settings`,
      payload,
    );
    yield all([
      put(actionCreators.updateDataSecuritySettingsSuccess(response.data)),
      put(sendSuccessMessage("SECURITY_SETTINGS_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.updateDataSecuritySettingsError(e.response));
  }
}

export function* onGetStaffGroups({ payload }) {
  try {
    const pagination = yield select(serviceSelectors.getStaffGroupsPagination);
    const currentPage = pagination.page;
    const page = payload.page ? payload.page : currentPage;
    const schema = yield parseSchema("StaffGroupDTO", { clientId: payload.clientId });

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/staff-group`,
      {
        params: {
          page,
        },
      },
    );

    yield put(actionCreators.GetStaffGroupsSuccess(response.data));
    yield put(actionCreators.SetStaffGroupSchema(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    console.warn("GetStaffGroupsError", e);

    yield put(actionCreators.GetStaffGroupsError(e.response));
  }
}

export function* onGetTotalStaffGroups({ payload }) {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `api/v1/service-admin/client/${payload.clientId}/staff-group`,
      {
        params: {
          unpaged: true,
        },
      },
    );

    yield put(actionCreators.getTotalStaffGroupsSuccess(response.data.content));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getTotalStaffGroupsError(e.response));
  }
}

export function* onAddStaffGroup({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error("Invalid payload provided");
    }
    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/staff-group`,
      payload.formData,
    );

    yield all([
      put(actionCreators.toggleAddStaffGroupModal(false)),
      put(
        actionCreators.getStaffGroups({
          clientId: payload.clientId,
        }),
      ),
      put(sendSuccessMessage("STAFF_GROUP_ADDED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getAddStaffGroupError(e.response));
  }
}

export function* onUpdateStaffGroup({ payload, type }) {
  const clientId = payload.clientId || null;
  const staffGroupId = payload.staffGroupId || null;
  const unassign = payload.hasOwnProperty("unassign") ? payload.unassign : null;

  const formValues = type === ReassignStaffGroup ? yield select(getFormValues("staff-groups")) : {};

  try {
    yield call(
      http.put,
      `/api/v1/service-admin/client/${clientId}/staff-group/${staffGroupId}/disable`,
      {
        unassign,
        destinationId: formValues.staffGroup || null,
      },
    );

    yield all([
      put(actionCreators.toggleDisableStaffGroupModal({ visible: false })),
      put(actionCreators.toggleReassignStaffGroupModal({ visible: false })),
      put(actionCreators.getStaffGroups({ clientId })),
      put(sendSuccessMessage("STAFF_GROUP_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.updateStaffGroupError(e.response));
  }
}

export function* onEnableStaffGroup({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.staffGroupId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/staff-group/${payload.staffGroupId}/enable`,
    );

    yield all([
      put(actionCreators.toggleEnableStaffGroupModal({ visible: false })),
      put(actionCreators.getStaffGroups({ clientId: payload.clientId })),
      put(sendSuccessMessage("STAFF_GROUP_ENABLED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.enableStaffGroupError(e.response));
  }
}

export function* onEditStaffGroup({ payload }) {
  try {
    if (!payload || !payload.clientId || !payload.staffGroupId || !payload.formData) {
      throw Error("Invalid payload provided");
    }
    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/staff-group/${payload.staffGroupId}`,
      payload.formData,
    );

    yield all([
      put(actionCreators.toggleEditStaffGroupModal(false)),
      put(actionCreators.getStaffGroups({ clientId: payload.clientId })),
      put(sendSuccessMessage("STAFF_GROUP_UPDATED")),
    ]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getAddStaffGroupError(e.response));
  }
}

export function* onGetStaffGroupsSchema() {
  try {
    const schema = yield parseSchema("StaffGroupDTO");
    yield put(actionCreators.setStaffGroupsSchema(schema));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetStaffGroupsError(e.response));
  }
}

export function* onSaveStaffGroupConfiguration({ payload }) {
  const clientId = payload.clientId || null;
  const formData = payload.formData || null;
  try {
    yield call(
      http.put,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/absence-management/staff-groups`,
      formData,
    );
    yield all([put(sendSuccessMessage("STAFF_GROUP_SAVED"))]);
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.getAddStaffGroupError(e.response));
  }
}

export function* onGetStaffGroupsConfiguration({ payload }) {
  const clientId = payload.clientId || null;

  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${clientId}/service-config/absence-management/staff-groups/effective`,
    );

    yield put(actionCreators.getStaffGroupsConfigurationSuccess(response.data));
  } catch (e) {
    console.error("Saga error: ", e);
    yield put(actionCreators.GetStaffGroupsError(e.response));
  }
}

export function* ServiceSaga() {
  yield all([
    // Staff Groups
    takeEvery(GetStaffGroups, onGetStaffGroups),
    takeEvery(GetTotalStaffGroups, onGetTotalStaffGroups),
    takeEvery(AddStaffGroup, onAddStaffGroup),
    takeEvery(UpdateStaffGroup, onUpdateStaffGroup),
    takeEvery(ReassignStaffGroup, onUpdateStaffGroup),
    takeEvery(EnableStaffGroup, onEnableStaffGroup),
    takeEvery(EditStaffGroup, onEditStaffGroup),
    takeEvery(GetStaffGroupsSchema, onGetStaffGroupsSchema),
    takeEvery(SaveStaffGroupConfiguration, onSaveStaffGroupConfiguration),
    takeEvery(GetStaffGroupsConfiguration, onGetStaffGroupsConfiguration),
    takeEvery(SetStaffGroupsSort, onGetStaffGroups),
    takeEvery(GetDataSecuritySettings, onGetDataSettings),
    takeEvery(UpdateDataSecuritySettings, onUpdateDataSettings),
    takeEvery(GetEmployeeSecuritySchema, onGetEmployeeSecuritySchema),

    // Service Settings
    spawn(serviceSettingsSaga),

    // Shift Patterns
    spawn(shiftPatternsSaga),

    // Nurse Declined Reasons
    spawn(nurseDeclinedReasonSagas),

    // EAP Signposting
    spawn(eapSignpostingSaga),

    // Absence Reasons
    spawn(absenceReasonsSaga),

    // Special Contacts
    spawn(specialContactsSaga),

    // Special Contacts Types
    spawn(specialContactsTypesSaga),

    // User Permissions
    spawn(userPermissionsSaga),

    // Reporting Parameters
    spawn(reportingParametersSaga),

    // Employee Identify
    spawn(employeeIdentifySaga),

    // Standard Question
    spawn(standardQuestionsSaga),

    // Dynamic Question
    spawn(dynamicQuestionsSaga),

    // Manager Sms
    spawn(managerSmsSettingsSaga),

    // Manager Sms Notifications
    spawn(managerSmsNotificationsSaga),

    // Manager Pager Restrictions
    spawn(managerPagerNotificationsSaga),

    // Email Notifications
    spawn(emailNotificationsSaga),

    // Employee Sms Prompts
    spawn(employeeSmsSettingsSaga),

    // Employee Sms Notifications
    spawn(employeeSmsNotificationsSaga),

    // Email Controls
    spawn(emailControlsSaga),

    // System Access
    spawn(systemAccessSaga),

    // Default Standard Script
    spawn(defaultStandardScriptsSaga),

    // Department Specific Script
    spawn(departmentSpecificScriptsSaga),

    // Alerts
    spawn(alertsSaga),

    // Alert Attachments
    spawn(alertAttachmentsSaga),

    // Approvable Change
    spawn(approvableChangeSaga),

    // Referrals
    spawn(referralsSaga),

    // Return to Work (RTW)
    spawn(rtwSaga),

    // Rtw Attachments
    spawn(rtwAttachmentsSaga),

    // Absence Monitoring
    spawn(absenceMonitoringSaga),

    // Reason Specific Script
    spawn(reasonSpecificScriptsSaga),

    // User Accounts
    spawn(userAccountsSaga),
  ]);
}

export default ServiceSaga;
